// Ported ~verbatim from nenda-autoplayer's src/components/Playback/Video/useStallLogger.ts.
// Pure React/DOM, zero framework dependencies — no changes needed.
import { useEffect, useRef } from "react";

interface UseStallLoggerProps {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  log: (msg: string) => void;
  onLongStall?: () => void;
  onStallRecovered?: (stallDurationMs: number) => void;
  longStallThresholdMs?: number;
}

export function useStallLogger({
  videoRef,
  log,
  onLongStall,
  onStallRecovered,
  longStallThresholdMs = 5000,
}: UseStallLoggerProps) {
  const stallTimer = useRef<number>();
  const stallStart = useRef<number>();
  const longStalled = useRef(false);
  const hasStarted = useRef(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    // `log` isn't called here in the upstream source either — kept as a prop for
    // interface parity with the ported call sites, silence the unused-var lint locally.
    void log;

    const onWaiting = () => {
      if (!hasStarted.current) return;
      // A stall timer is already running.
      if (stallStart.current != undefined) {
        return;
      }
      stallStart.current = performance.now();
      longStalled.current = false;

      stallTimer.current = window.setTimeout(() => {
        longStalled.current = true;
        onLongStall?.();
      }, longStallThresholdMs);
    };

    const onPlaying = () => {
      if (!hasStarted.current) {
        hasStarted.current = true;
        return;
      }
      if (stallTimer.current) {
        clearTimeout(stallTimer.current);
        stallTimer.current = undefined;
      }

      if (stallStart.current != null) {
        const duration = performance.now() - stallStart.current;
        if (longStalled.current) {
          onStallRecovered?.(duration);
        }
      }

      stallStart.current = undefined;
      longStalled.current = false;
    };

    video.addEventListener("waiting", onWaiting);
    video.addEventListener("playing", onPlaying);

    return () => {
      video.removeEventListener("waiting", onWaiting);
      video.removeEventListener("playing", onPlaying);
      if (stallTimer.current) {
        clearTimeout(stallTimer.current);
      }
    };
  }, [videoRef, log, onLongStall, onStallRecovered, longStallThresholdMs]);
}
